
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAddPortfolioForm, PortfolioFormValues } from '@/hooks/useAddPortfolioForm';
import PortfolioForm from './portfolio/form/PortfolioForm';
import { useToast } from '@/hooks/use-toast';
import { useFirebase } from '@/context/FirebaseProvider'; // Changed from DataContext

interface AddPortfolioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddPortfolio: (portfolioData: PortfolioFormValues) => Promise<any>;
}

const AddPortfolioModal: React.FC<AddPortfolioModalProps> = ({ 
  open, 
  onOpenChange,
  onAddPortfolio
}) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { firebaseUser, userData } = useFirebase(); // Use firebaseUser or userData for auth check
  
  const { form, onSubmit } = useAddPortfolioForm(
    async (data: PortfolioFormValues) => {
      console.log('Portfolio form submitted with data:', data);
      
      if (!data.name || data.name.trim() === '') {
        toast({
          title: "Validation error",
          description: "Portfolio name is required",
          variant: "destructive"
        });
        return;
      }
      
      // Verify user is logged in before attempting to create portfolio
      if (!firebaseUser?.uid && !userData?.id) { // Check firebaseUser or userData
        toast({
          title: "Authentication error",
          description: "You must be logged in to create a portfolio",
          variant: "destructive"
        });
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        console.log('Calling onAddPortfolio with:', data);
        const result = await onAddPortfolio(data);
        console.log('Portfolio creation result:', result);
        
        if (result?.success) {
          form.reset(); // Reset form after successful submission
          onOpenChange(false); // Close modal after successful submission
          
          toast({
            title: "Portfolio created",
            description: `Portfolio "${data.name}" has been created successfully.`
          });
        } else if (result?.error) {
          throw result.error;
        }
      } catch (error) {
        console.error('Error in portfolio form submission:', error);
        
        toast({
          title: "Form submission error",
          description: error instanceof Error ? error.message : "There was an error processing your request. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submit handler called');
    
    // Add pre-submit validation for auth
    if (!firebaseUser?.uid && !userData?.id) { // Check firebaseUser or userData
      toast({
        title: "Authentication error",
        description: "You must be logged in to create a portfolio. Please refresh the page and try again.",
        variant: "destructive"
      });
      return;
    }
    
    form.handleSubmit(onSubmit)(e);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      // Prevent closing the dialog if currently submitting
      if (isSubmitting && !isOpen) return;
      // Reset form when closing the modal
      if (!isOpen) {
        form.reset();
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent className={cn(
        "sm:max-w-[550px]",
        isMobile && "h-[90vh] p-0 flex flex-col"
      )}>
        <DialogHeader className={cn("sm:p-6", isMobile && "p-4")}>
          <DialogTitle className="text-xl font-semibold">Add New Portfolio</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className={cn(
          "flex-1",
          isMobile && "px-4 pb-20"
        )}>
          <div className={cn("px-6 pb-6", isMobile && "px-0")}>
            <PortfolioForm 
              form={form} 
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting} 
            />
          </div>
        </ScrollArea>
        
        <DialogFooter className={cn(
          "sm:px-6 sm:py-4",
          isMobile && "px-4 py-3 border-t bg-white w-full fixed bottom-0 left-0 right-0"
        )}>
          <Button 
            type="submit"
            onClick={handleFormSubmit}
            disabled={isSubmitting}
            className={cn(isMobile && "w-full")}
          >
            {isSubmitting ? "Creating..." : "Create Portfolio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPortfolioModal;
